/**
 * My Access — Track 5.5 (self-service surface).
 *
 * Every user sees their own grants and can request new access. Backend:
 *   /api/cdm/me/access            — list own grants
 *   /api/cdm/me/access/requests   — list own pending/decided
 *   /api/cdm/me/access/request    — submit a new request
 *
 * No admin gate. The Request form is intentionally simple: pick a role,
 * pick a scope, write a reason — most requests will be the
 * home_org_cascade default.
 */
import { useEffect, useState } from 'react'
import {
  User, RefreshCw, Send, Clock, CheckCircle, AlertTriangle, X,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface Grant {
  id: string
  role: string
  scope_kind: string
  scope_filter: Record<string, any>
  granted_by: string
  granted_at: string | null
  expires_at: string | null
  days_until_expiry: number | null
  last_used_at: string | null
  last_attested_at: string | null
  reason: string
  state: string
}

interface Rollup {
  active_grants: number
  soonest_expiry_days: number | null
  last_attestation: string | null
}

interface Request_ {
  id: string
  role: string
  scope_kind: string
  scope_filter: Record<string, any>
  reason: string
  state: string
  decision: string | null
  decision_notes: string | null
  requested_at: string | null
  decided_at: string | null
  approver_id: string | null
}

const ROLES = ['viewer', 'analyst', 'operator', 'admin'] as const
const SCOPE_KINDS = ['home_org_cascade', 'explicit_grant', 'region_view', 'global_view'] as const

function expiryClass(days: number | null): string {
  if (days === null) return 'text-text-muted'
  if (days < 7)  return 'text-rose-light font-semibold'
  if (days < 30) return 'text-amber-light'
  return 'text-emerald-light'
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—'
  try { return new Date(iso).toISOString().slice(0, 10) } catch { return iso }
}

export default function MyAccessPage() {
  const [email, setEmail] = useState('')
  const [grants, setGrants] = useState<Grant[]>([])
  const [rollup, setRollup] = useState<Rollup | null>(null)
  const [requests, setRequests] = useState<Request_[]>([])
  const [loading, setLoading] = useState(false)
  const [message, setMessage] = useState('')

  const [formRole, setFormRole] = useState<string>('viewer')
  const [formScopeKind, setFormScopeKind] = useState<string>('home_org_cascade')
  const [formScopeFilter, setFormScopeFilter] = useState<string>('{}')
  const [formReason, setFormReason] = useState('')
  const [submitting, setSubmitting] = useState(false)

  const load = () => {
    setLoading(true)
    api.get('/api/cdm/me/access')
      .then(r => {
        setGrants(r.data?.grants || [])
        setRollup(r.data?.rollup || null)
        setEmail(r.data?.user_email || '')
      })
      .catch(e => setMessage(`Load failed: ${e.response?.data?.detail || e.message}`))
      .finally(() => setLoading(false))

    api.get('/api/cdm/me/access/requests')
      .then(r => setRequests(r.data?.requests || []))
      .catch(() => {})
  }

  useEffect(() => { load() }, [])

  const submit = async () => {
    if (!formReason.trim() || formReason.trim().length < 5) {
      setMessage('Reason is required (at least 5 characters)')
      return
    }
    let scopeFilterObj: any = {}
    try { scopeFilterObj = JSON.parse(formScopeFilter || '{}') } catch {
      setMessage('Scope filter must be valid JSON')
      return
    }
    setSubmitting(true)
    setMessage('Submitting request...')
    try {
      const { data } = await api.post('/api/cdm/me/access/request', {
        role: formRole,
        scope_kind: formScopeKind,
        scope_filter: scopeFilterObj,
        reason: formReason.trim(),
      })
      setMessage(`Request submitted (id: ${data.id?.slice(0,8)}). An admin will review it.`)
      setFormReason('')
      setFormScopeFilter('{}')
      load()
    } catch (e: any) {
      setMessage(`Submit failed: ${e.response?.data?.detail || e.message}`)
    } finally {
      setSubmitting(false)
    }
  }

  const cancel = async (req: Request_) => {
    if (!confirm(`Cancel pending request for ${req.role} / ${req.scope_kind}?`)) return
    try {
      await api.post(`/api/cdm/me/access/requests/${req.id}/cancel`)
      setMessage('Request cancelled')
      load()
    } catch (e: any) {
      setMessage(`Cancel failed: ${e.response?.data?.detail || e.message}`)
    }
  }

  const showScopeFilterField =
    formScopeKind === 'region_view' || formScopeKind === 'explicit_grant'

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <User size={18} className="text-cyan" /> YOUR ACCESS
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          {email ? email : 'self-service · request access · view your grants'}
        </span>
        <div className="ml-auto">
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      {/* Rollup tile */}
      {rollup && (
        <div className="grid grid-cols-3 gap-2">
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Active grants</div>
            <div className="text-2xl font-mono text-emerald-light">{rollup.active_grants}</div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Soonest expiry</div>
            <div className={clsx(
              "text-2xl font-mono",
              expiryClass(rollup.soonest_expiry_days)
            )}>
              {rollup.soonest_expiry_days === null ? '—' : `${Math.round(rollup.soonest_expiry_days)}d`}
            </div>
          </div>
          <div className="card p-3">
            <div className="text-[10px] uppercase tracking-widest text-text-muted">Last attested</div>
            <div className="text-sm font-mono text-text-secondary mt-1">
              {fmtDate(rollup.last_attestation)}
            </div>
          </div>
        </div>
      )}

      {rollup && rollup.soonest_expiry_days !== null && rollup.soonest_expiry_days < 14 && (
        <div className="card p-3 border-amber/40 bg-amber/5 flex items-center gap-2">
          <AlertTriangle size={16} className="text-amber-light" />
          <span className="text-[12px] text-text-secondary">
            One or more of your grants expires in {Math.round(rollup.soonest_expiry_days)} days. Submit
            a renewal request below to avoid losing access.
          </span>
        </div>
      )}

      {/* Active grants table */}
      <div>
        <h2 className="text-sm font-semibold text-text-primary mb-2">Active grants</h2>
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised">
              <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Scope</th>
                <th className="text-left px-3 py-2">Granted by</th>
                <th className="text-left px-3 py-2">Granted</th>
                <th className="text-left px-3 py-2">Expires</th>
                <th className="text-left px-3 py-2">State</th>
                <th className="text-left px-3 py-2">Reason</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {grants.length === 0 && !loading && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-text-muted">
                  You have no grants yet. Submit a request below.
                </td></tr>
              )}
              {grants.map(g => (
                <tr key={g.id}>
                  <td className="px-3 py-1.5">
                    <span className={clsx(
                      'px-1.5 py-0.5 rounded text-[10px] font-mono',
                      g.role === 'admin'    && 'bg-rose/20 text-rose-light',
                      g.role === 'operator' && 'bg-amber/20 text-amber-light',
                      g.role === 'analyst'  && 'bg-blue/20 text-blue-light',
                      g.role === 'viewer'   && 'bg-surface-raised text-text-secondary',
                    )}>{g.role}</span>
                  </td>
                  <td className="px-3 py-1.5 text-text-secondary">
                    <div>{g.scope_kind}</div>
                    {Object.keys(g.scope_filter || {}).length > 0 && (
                      <div className="text-[10px] text-text-muted font-mono">{JSON.stringify(g.scope_filter)}</div>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-text-muted text-[10px] font-mono">{g.granted_by}</td>
                  <td className="px-3 py-1.5 text-text-muted">{fmtDate(g.granted_at)}</td>
                  <td className={clsx('px-3 py-1.5', expiryClass(g.days_until_expiry))}>
                    {fmtDate(g.expires_at)}
                    {g.days_until_expiry !== null && (
                      <div className="text-[10px]">{Math.round(g.days_until_expiry)}d left</div>
                    )}
                  </td>
                  <td className="px-3 py-1.5">
                    <span className={clsx(
                      'text-[10px] uppercase tracking-widest',
                      g.state === 'active'    && 'text-emerald-light',
                      g.state === 'suspended' && 'text-amber-light',
                      g.state === 'revoked'   && 'text-text-muted',
                    )}>{g.state}</span>
                  </td>
                  <td className="px-3 py-1.5 text-text-muted text-[10px]">{g.reason}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pending requests */}
      <div>
        <h2 className="text-sm font-semibold text-text-primary mb-2 flex items-center gap-2">
          Your requests <span className="text-text-muted text-[10px]">({requests.length})</span>
        </h2>
        <div className="card p-0 overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-surface-raised">
              <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                <th className="text-left px-3 py-2">Requested</th>
                <th className="text-left px-3 py-2">Role</th>
                <th className="text-left px-3 py-2">Scope</th>
                <th className="text-left px-3 py-2">Reason</th>
                <th className="text-left px-3 py-2">State</th>
                <th className="text-left px-3 py-2">Decided by</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {requests.length === 0 && (
                <tr><td colSpan={7} className="px-3 py-6 text-center text-text-muted">
                  No requests yet.
                </td></tr>
              )}
              {requests.map(r => (
                <tr key={r.id}>
                  <td className="px-3 py-1.5 text-text-muted">{fmtDate(r.requested_at)}</td>
                  <td className="px-3 py-1.5 font-mono text-text-secondary">{r.role}</td>
                  <td className="px-3 py-1.5 text-text-secondary">{r.scope_kind}</td>
                  <td className="px-3 py-1.5 text-text-muted text-[10px]">{r.reason}</td>
                  <td className="px-3 py-1.5">
                    <span className={clsx(
                      'inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] uppercase',
                      r.state === 'pending'   && 'bg-amber/20 text-amber-light',
                      r.state === 'approved'  && 'bg-emerald/20 text-emerald-light',
                      r.state === 'rejected'  && 'bg-rose/20 text-rose-light',
                      r.state === 'cancelled' && 'bg-surface-raised text-text-muted',
                    )}>
                      {r.state === 'pending' && <Clock size={10} />}
                      {r.state === 'approved' && <CheckCircle size={10} />}
                      {r.state === 'rejected' && <X size={10} />}
                      {r.state}
                    </span>
                    {r.decision_notes && (
                      <div className="text-[10px] text-text-muted mt-0.5">{r.decision_notes}</div>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-text-muted text-[10px] font-mono">{r.approver_id || '—'}</td>
                  <td className="px-3 py-1.5 text-right">
                    {r.state === 'pending' && (
                      <button type="button" onClick={() => cancel(r)}
                              className="text-text-muted hover:text-rose-light text-[10px]">
                        Cancel
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Request form */}
      <div className="card p-3 space-y-3">
        <div className="flex items-center gap-2">
          <Send size={14} className="text-cyan" />
          <h2 className="text-sm font-semibold text-text-primary">Request access</h2>
        </div>
        <p className="text-[11px] text-text-muted">
          Requests usually need approval from a security admin within 24 hours.
          You'll see the result here once decided. Default expiry is set by role tier
          (admin/global 90d, operator/analyst 180d, viewer 365d, viewer-explicit 30d).
        </p>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-text-muted">Role</span>
            <select value={formRole} onChange={e => setFormRole(e.target.value)}
                    className="input w-full text-xs">
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-text-muted">Scope kind</span>
            <select value={formScopeKind} onChange={e => setFormScopeKind(e.target.value)}
                    className="input w-full text-xs">
              {SCOPE_KINDS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </label>
        </div>
        {showScopeFilterField && (
          <label className="block">
            <span className="text-[10px] uppercase tracking-widest text-text-muted">
              Scope filter (JSON, e.g. {`{"region":"EMEA"}`})
            </span>
            <textarea value={formScopeFilter}
                      onChange={e => setFormScopeFilter(e.target.value)}
                      rows={2} spellCheck={false}
                      className="w-full font-mono text-[11px] bg-surface-raised p-2 rounded border border-border" />
          </label>
        )}
        <label className="block">
          <span className="text-[10px] uppercase tracking-widest text-text-muted">
            Reason (be specific — this becomes the audit trail)
          </span>
          <textarea value={formReason}
                    onChange={e => setFormReason(e.target.value)}
                    rows={3}
                    placeholder="I need analyst access to investigate Q3 incident tickets..."
                    className="w-full text-xs bg-surface-raised p-2 rounded border border-border" />
        </label>
        <div className="flex justify-end">
          <button type="button" onClick={submit} disabled={submitting}
                  className="btn-primary">
            <Send size={11} /> Submit request
          </button>
        </div>
      </div>

      <p className="text-[10px] text-text-muted text-center">
        Banking-grade RBAC: every grant is bounded, every change audited.
        Lost or expired grants? Request a renewal here — admins see all pending requests on /app/admin/access.
      </p>
    </div>
  )
}
